import React, { useEffect, useState } from "react";
import CrudManager from "./CrudManager";
import { api, CATEGORIES } from "@/lib/api";

const PUB = { key: "status", label: "Status", type: "select", options: [{ value: "published", label: "Published (live)" }, { value: "draft", label: "Draft (hidden)" }], noEmpty: true };
const SEO = { key: "seo", label: "SEO (page title & description)", type: "pairs", fields: [] };
const seoFields = [{ key: "seo_title", label: "SEO page title", help: "Leave blank to use the default." }, { key: "seo_description", label: "SEO description", type: "textarea", rows: 2 }];

function withSeo(item) {
  return { ...item, seo_title: item.seo?.title, seo_description: item.seo?.description };
}

function useProjectOptions() {
  const [opts, setOpts] = useState([]);
  useEffect(() => { api.get("/admin/c/projects").then((r) => setOpts(r.data.map((p) => ({ value: p.id, label: p.title })))); }, []);
  return opts;
}

export function ProjectsAdmin() {
  return (
    <CrudManager col="projects" title="Projects" publicPath="/projects"
      sub="Add your completed work. Published projects appear in the public gallery automatically; turn on “Show on homepage” to feature them."
      defaults={{ status: "draft", category: "custom-homes", gallery: [], before_after: [], features: [], featured: false, location: "" }}
      schema={[
        { key: "title", label: "Project title" }, { key: "slug", label: "URL slug", help: "Created automatically from the title." },
        { key: "category", label: "Project type", type: "select", options: CATEGORIES, noEmpty: true }, { key: "location", label: "City or general location" },
        PUB, { key: "featured", label: "Show on homepage", type: "switch" },
        { key: "year", label: "Completion year (optional)" }, { key: "services", label: "Related services (slugs)", type: "list", rows: 2, help: "e.g. new-home-construction" },
        { key: "cover", label: "Featured image", type: "image" },
        { key: "description", label: "Project description", type: "textarea" }, { key: "scope", label: "Scope of work", type: "textarea", rows: 3 },
        { key: "features", label: "Construction features", type: "list" },
        { key: "gallery", label: "Photo gallery (drag order with arrows)", type: "images" },
        { key: "before_after", label: "Before & after comparisons", type: "pairs", fields: [{ key: "before", label: "Before", type: "image" }, { key: "after", label: "After", type: "image" }, { key: "caption", label: "Caption", full: true }] },
        ...seoFields,
      ]}
      display={(x) => ({ title: x.title, sub: `${CATEGORIES.find((c) => c.value === x.category)?.label || ""} · ${x.location || ""} · ${(x.gallery || []).length} photos`, image: x.cover })}
    />
  );
}

export function ServicesAdmin() {
  return (
    <CrudManager col="services" title="Services" publicPath="/services" sub="Each service has its own page. Set a service to Draft to hide it from the website."
      defaults={{ status: "draft", bullets: [], faqs: [], gallery: [], icon: "home" }}
      schema={[
        { key: "title", label: "Service name" }, { key: "slug", label: "URL slug" }, PUB,
        { key: "icon", label: "Icon", type: "select", options: ["home", "warehouse", "hammer", "wrench", "building", "trees"], noEmpty: true },
        { key: "short", label: "Short summary (homepage card)", type: "textarea", rows: 2 },
        { key: "card_image", label: "Card image", type: "image" }, { key: "hero_image", label: "Page hero image", type: "image" },
        { key: "description", label: "Full description", type: "textarea", rows: 6 }, { key: "bullets", label: "What's included", type: "list" },
        { key: "gallery", label: "Service photos", type: "images" },
        { key: "faqs", label: "Service FAQs", type: "pairs", fields: [{ key: "q", label: "Question", full: true }, { key: "a", label: "Answer", type: "textarea", rows: 2 }] },
        ...seoFields,
      ]}
    />
  );
}

export function VideosAdmin() {
  const projects = useProjectOptions();
  return (
    <CrudManager col="videos" title="Videos" sub="Upload a video file or paste a YouTube/Vimeo link. Videos only load when a visitor presses play."
      defaults={{ status: "draft", kind: "youtube" }}
      schema={[
        { key: "title", label: "Video title" }, PUB,
        { key: "kind", label: "Video source", type: "select", options: [{ value: "youtube", label: "YouTube / Vimeo link" }, { value: "upload", label: "Uploaded file" }], noEmpty: true },
        { key: "url", label: "YouTube/Vimeo URL (for links)", help: "For uploaded files use the field below." },
        { key: "upload", label: "Uploaded video file", type: "video", help: "Choose an uploaded video from the media library." },
        { key: "poster", label: "Thumbnail / poster image", type: "image" },
        { key: "project_id", label: "Associated project", type: "select", options: projects }, { key: "featured", label: "Featured video", type: "switch" },
        { key: "description", label: "Description", type: "textarea", rows: 3 },
      ]}
      display={(x) => ({ title: x.title, sub: x.kind === "upload" ? "Uploaded file" : x.url, image: x.poster })}
    />
  );
}

export function ReviewsAdmin() {
  const projects = useProjectOptions();
  return (
    <CrudManager col="reviews" title="Reviews" sub="Only add authentic customer reviews. A review appears on the site only when its status is Approved."
      defaults={{ status: "pending", show_name: false }}
      schema={[
        { key: "name", label: "Customer name" }, { key: "show_name", label: "Customer gave permission to show their name", type: "switch" },
        { key: "status", label: "Status", type: "select", options: [{ value: "approved", label: "Approved (live)" }, { value: "pending", label: "Pending (hidden)" }], noEmpty: true },
        { key: "rating", label: "Star rating (only if from an authentic review)", type: "number" }, { key: "source", label: "Source (e.g. Google)" },
        { key: "project_id", label: "Related project", type: "select", options: projects }, { key: "video_url", label: "Customer video URL (optional)" },
        { key: "text", label: "Review text", type: "textarea" },
      ]}
      display={(x) => ({ title: x.name, sub: x.text })}
    />
  );
}

export function FaqsAdmin() {
  return (
    <CrudManager col="faqs" title="FAQs" publicPath={null}
      sub="Add, edit or delete questions shown on the FAQ page (/faq). Only Published questions appear on the website, and the AI assistant uses them automatically. Entries in [brackets] are drafts waiting for your answer."
      defaults={{ status: "published", category: "Getting Started" }}
      schema={[{ key: "question", label: "Question", full: true },
        { key: "category", label: "Category", type: "select", noEmpty: true, options: ["Getting Started", "About Ironbark", "Services", "Pricing & Budget", "Timeline & Process", "Permits & Codes", "Design & Materials", "Warranty & After Move-In", "General"] },
        PUB, { key: "answer", label: "Answer", type: "textarea", rows: 6 }]}
      display={(x) => ({ title: x.question, sub: `${x.category || "General"} · ${x.answer}` })}
    />
  );
}

export function ProcessAdmin() {
  return (
    <CrudManager col="process_steps" title="Process Steps" sub="The stages shown on the homepage and Process page."
      defaults={{ status: "published" }}
      schema={[{ key: "number", label: "Step number", type: "number" }, { key: "title", label: "Stage title" }, PUB, { key: "text", label: "Description", type: "textarea" }]}
    />
  );
}

export function AreasAdmin() {
  return (
    <CrudManager col="service_areas" title="Service Areas" publicPath="/service-areas"
      sub="Only publish areas you actually serve. Each published area gets its own local page — add unique, useful content to avoid thin pages."
      defaults={{ status: "draft" }}
      schema={[{ key: "name", label: "City / area name" }, { key: "slug", label: "URL slug" }, PUB, { key: "image", label: "Hero image", type: "image" },
        { key: "intro", label: "Intro", type: "textarea", rows: 2 }, { key: "body", label: "Page content", type: "textarea", rows: 8 }, ...seoFields]}
      display={(x) => ({ title: x.name, sub: x.intro })}
    />
  );
}

export function AlbumsAdmin() {
  return (
    <CrudManager col="albums" title="Albums" sub="Group photos into albums, then assign photos to an album in the Photo Gallery."
      defaults={{}} schema={[{ key: "name", label: "Album name" }, { key: "cover", label: "Cover image", type: "image" }, { key: "description", label: "Description", type: "textarea", rows: 2 }]}
      display={(x) => ({ title: x.name, sub: x.description, image: x.cover })}
    />
  );
}

export { withSeo, SEO };
